import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { BookOpen, Plus, X, Loader2 } from 'lucide-react';

import { API } from '../utils/api';

const categoryTypeLabels = {
  current_asset: 'Current Asset', fixed_asset: 'Fixed Asset', other_asset: 'Other Asset',
  current_liability: 'Current Liability', long_term_liability: 'Long-term Liability',
  equity: 'Equity', income: 'Income', expense: 'Expense'
};

export const JournalEntryDialog = ({ open, onOpenChange, getAuthHeaders, categories, llcs, onCategoryCreated, editEntry = null, onSaved }) => {
  const [formLoading, setFormLoading] = useState(false);
  const [llcId, setLlcId] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [description, setDescription] = useState('');
  const [reference, setReference] = useState('');
  const [lines, setLines] = useState([{ category_id: '', amount: '', description: '' }]);
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [newCatType, setNewCatType] = useState('expense');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);

  // Track if form has unsaved changes
  const hasChanges = () => {
    if (editEntry) {
      // Check if anything changed from original
      if (llcId !== editEntry.llc_id) return true;
      if (date !== editEntry.date) return true;
      if (description !== editEntry.description) return true;
      if ((reference || '') !== (editEntry.reference || '')) return true;
      if (lines.length !== editEntry.lines.length) return true;
      for (let i = 0; i < lines.length; i++) {
        if (lines[i].category_id !== editEntry.lines[i]?.category_id) return true;
        if (lines[i].amount !== editEntry.lines[i]?.amount?.toString()) return true;
        if ((lines[i].description || '') !== (editEntry.lines[i]?.description || '')) return true;
      }
      return false;
    } else {
      // For new entry, check if user has entered anything
      return description.trim() !== '' || 
             lines.some(l => l.category_id || l.amount || l.description);
    }
  };

  useEffect(() => {
    if (open) {
      if (editEntry) {
        // Populate form with existing entry data
        setLlcId(editEntry.llc_id);
        setDate(editEntry.date);
        setDescription(editEntry.description);
        setReference(editEntry.reference || '');
        setLines(editEntry.lines.map(l => ({
          category_id: l.category_id,
          amount: l.amount.toString(),
          description: l.description || ''
        })));
      } else {
        // New entry defaults
        if (llcs.length > 0 && !llcId) setLlcId(llcs[0].id);
      }
    }
  }, [open, editEntry, llcs]);

  const resetForm = () => {
    setLlcId(llcs.length > 0 ? llcs[0].id : '');
    setDate(new Date().toISOString().split('T')[0]);
    setDescription('');
    setReference('');
    setLines([{ category_id: '', amount: '', description: '' }]);
    setShowNewCategory(false);
  };

  const addLine = () => setLines([...lines, { category_id: '', amount: '', description: '' }]);
  const removeLine = (idx) => { if (lines.length > 1) setLines(lines.filter((_, i) => i !== idx)); };
  const updateLine = (idx, field, value) => {
    const newLines = [...lines];
    newLines[idx] = { ...newLines[idx], [field]: value };
    setLines(newLines);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!llcId || !description) { toast.error('Fill required fields'); return; }
    const validLines = lines.filter(l => l.category_id && l.amount);
    if (validLines.length === 0) { toast.error('Add at least one line'); return; }
    setFormLoading(true);
    try {
      const payload = {
        llc_id: llcId, date, description, reference: reference || null,
        lines: validLines.map(l => ({ category_id: l.category_id, amount: parseFloat(l.amount), description: l.description || null }))
      };
      
      if (editEntry) {
        await axios.put(`${API}/journal-entries/${editEntry.id}`, payload, { headers: getAuthHeaders() });
        toast.success('Journal entry updated');
      } else {
        await axios.post(`${API}/journal-entries`, payload, { headers: getAuthHeaders() });
        toast.success('Journal entry created');
      }
      onOpenChange(false);
      resetForm();
      if (onSaved) onSaved();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!editEntry) return;
    setFormLoading(true);
    try {
      await axios.delete(`${API}/journal-entries/${editEntry.id}`, { headers: getAuthHeaders() });
      toast.success('Journal entry deleted');
      setDeleteConfirmOpen(false);
      onOpenChange(false);
      resetForm();
      if (onSaved) onSaved();
    } catch (error) {
      toast.error('Failed to delete');
    } finally {
      setFormLoading(false);
    }
  };

  const handleClose = () => { 
    if (hasChanges()) {
      setCloseConfirmOpen(true);
    } else {
      onOpenChange(false); 
      resetForm(); 
    }
  };
  
  const confirmClose = () => {
    setCloseConfirmOpen(false);
    onOpenChange(false);
    resetForm();
  };

  const handleCreateCategory = async () => {
    if (!newCatName.trim()) { toast.error('Enter category name'); return; }
    setFormLoading(true);
    try {
      const response = await axios.post(`${API}/categories`, { name: newCatName, category_type: newCatType }, { headers: getAuthHeaders() });
      toast.success('Category created');
      if (onCategoryCreated) onCategoryCreated(response.data);
      setShowNewCategory(false);
      setNewCatName('');
      setNewCatType('expense');
    } catch (error) {
      toast.error('Failed to create category');
    } finally {
      setFormLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) handleClose(); }}>
        <DialogContent 
          className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto"
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="font-serif text-xl flex items-center gap-2">
              <BookOpen className="w-5 h-5" />
              {editEntry ? 'Edit Journal Entry' : 'New Journal Entry'}
            </DialogTitle>
            <DialogDescription>
              {editEntry ? 'Update this journal entry' : 'Create entries for starting balances or adjustments'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>LLC *</Label>
                  <Select value={llcId} onValueChange={setLlcId}>
                    <SelectTrigger><SelectValue placeholder="Select LLC" /></SelectTrigger>
                    <SelectContent>{llcs.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Date *</Label>
                  <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Description *</Label>
                  <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Opening balance" required />
                </div>
                <div className="space-y-2">
                  <Label>Reference #</Label>
                  <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Optional" />
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Entry Lines</Label>
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={() => setShowNewCategory(!showNewCategory)}>
                      <Plus className="w-3 h-3 mr-1" />New Category
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={addLine}><Plus className="w-3 h-3 mr-1" />Add Line</Button>
                  </div>
                </div>
                
                {showNewCategory && (
                  <div className="border rounded-lg p-3 bg-amber-50 border-amber-200 space-y-3">
                    <Label className="text-amber-800">Quick Add Category</Label>
                    <div className="flex gap-2">
                      <Input value={newCatName} onChange={(e) => setNewCatName(e.target.value)} placeholder="Category name" className="flex-1 bg-white" />
                      <Select value={newCatType} onValueChange={setNewCatType}>
                        <SelectTrigger className="w-40 bg-white"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="current_asset">Current Asset</SelectItem>
                          <SelectItem value="fixed_asset">Fixed Asset</SelectItem>
                          <SelectItem value="other_asset">Other Asset</SelectItem>
                          <SelectItem value="current_liability">Current Liability</SelectItem>
                          <SelectItem value="long_term_liability">Long-term Liability</SelectItem>
                          <SelectItem value="equity">Equity</SelectItem>
                          <SelectItem value="income">Income</SelectItem>
                          <SelectItem value="expense">Expense</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button type="button" size="sm" onClick={handleCreateCategory} disabled={formLoading} className="bg-amber-600 hover:bg-amber-700">
                        {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add'}
                      </Button>
                      <Button type="button" size="sm" variant="ghost" onClick={() => setShowNewCategory(false)}><X className="w-4 h-4" /></Button>
                    </div>
                  </div>
                )}
                
                <div className="border rounded-lg p-3 space-y-3 bg-slate-50 max-h-[300px] overflow-y-auto">
                  {lines.map((line, idx) => (
                    <div key={idx} className="flex gap-2 items-start">
                      <div className="flex-1">
                        <Select value={line.category_id} onValueChange={(v) => updateLine(idx, 'category_id', v)}>
                          <SelectTrigger className="bg-white"><SelectValue placeholder="Category" /></SelectTrigger>
                          <SelectContent>{[...categories].sort((a, b) => {
                            // Sort by type first, then by name
                            const typeOrder = ['current_asset', 'fixed_asset', 'other_asset', 'current_liability', 'long_term_liability', 'equity', 'income', 'expense'];
                            const typeA = typeOrder.indexOf(a.category_type);
                            const typeB = typeOrder.indexOf(b.category_type);
                            if (typeA !== typeB) return typeA - typeB;
                            return a.name.localeCompare(b.name);
                          }).map((c) => <SelectItem key={c.id} value={c.id}>{categoryTypeLabels[c.category_type] || c.category_type}: {c.name}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <Input type="number" step="0.01" value={line.amount} onChange={(e) => updateLine(idx, 'amount', e.target.value)} placeholder="+/- Amt" className="w-28 bg-white" />
                      <Input value={line.description} onChange={(e) => updateLine(idx, 'description', e.target.value)} placeholder="Note" className="flex-1 bg-white" />
                      {lines.length > 1 && <Button type="button" variant="ghost" size="icon" className="h-9 w-9 text-rose-500" onClick={() => removeLine(idx)}><X className="w-4 h-4" /></Button>}
                    </div>
                  ))}
                  {/* Balance indicator */}
                  {(() => {
                    const total = lines.reduce((sum, l) => sum + (parseFloat(l.amount) || 0), 0);
                    const isBalanced = Math.abs(total) < 0.01;
                    return (
                      <div className={`mt-3 pt-3 border-t flex justify-between items-center text-sm font-medium ${isBalanced ? 'text-emerald-700' : 'text-rose-600'}`}>
                        <span>Balance:</span>
                        <span className="font-mono">
                          {isBalanced ? '✓ Balanced' : `Out of balance: $${total.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`}
                        </span>
                      </div>
                    );
                  })()}
                </div>
                <p className="text-xs text-slate-500">+ = debit (assets/expenses), - = credit (income/liabilities). Entry must balance to save.</p>
              </div>
            </div>
            <DialogFooter className="flex justify-between">
              <div>
                {editEntry && (
                  <Button type="button" variant="destructive" onClick={() => setDeleteConfirmOpen(true)} disabled={formLoading}>
                    Delete
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={handleClose}>Cancel</Button>
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800" disabled={formLoading}>
                  {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : (editEntry ? 'Update' : 'Create')}
                </Button>
              </div>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Journal Entry</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this journal entry? This will also remove all associated transactions.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-rose-600 hover:bg-rose-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={closeConfirmOpen} onOpenChange={setCloseConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard Changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to close this dialog? Your changes will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Editing</AlertDialogCancel>
            <AlertDialogAction onClick={confirmClose} className="bg-rose-600 hover:bg-rose-700">Discard Changes</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default JournalEntryDialog;
